/** SYSTEM HEALTH DASHBOARD — application · database · API failures · background jobs · integrations · logs. Route: /admin/system-health */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Play } from "lucide-react";
import { ComponentCard, STATUS, StatusDot, TrafficChart, fmtSince } from "@/components/admin/systemHealthParts";
import { ReleaseArtifacts } from "@/components/admin/ReleaseArtifacts";
import { ApiFailuresTab, ApplicationTab, DatabaseTab, HistoryTab, IntegrationsTab, JobsTab, LogsTab } from "@/components/admin/systemHealthTabs";

const TABS = [
  { id: "application", label: "Application" }, { id: "database", label: "Database" }, { id: "api", label: "API failures" },
  { id: "jobs", label: "Background jobs" }, { id: "integrations", label: "Integrations" }, { id: "logs", label: "Logs" }, { id: "history", label: "History" },
];
const ADMIN = new Set(["super_admin", "admin", "management"]);

export default function SystemHealth() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "application";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/system-health/overview"); setData(data); setErr(null); }
    catch (e) { setErr(e.response?.data?.detail || "System Health unavailable"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!auto) return undefined; const t = setInterval(load, 30000); return () => clearInterval(t); }, [auto, load]);

  const runChecks = async () => {
    setBusy(true);
    try { const { data } = await api.post("/system-health/checks/run"); setData(data); toast.success(`Checks complete · ${data.label}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to run checks"); }
    finally { setBusy(false); }
  };
  const setTab = (id) => setParams({ tab: id });
  const overall = data?.overall || "operational";
  const t = STATUS[overall];
  const isAdmin = ADMIN.has(user?.role);
  const comp = (k) => data?.components?.find((c) => c.key === k);

  return (
    <div className="space-y-6 os-fade-in" data-testid="system-health-page">
      <div className={`os-card p-5 md:p-6 flex flex-wrap items-center gap-4 border-l-4 ${t.border}`} data-testid="sh-overall-banner">
        <StatusDot status={overall} ping size="h-4 w-4" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)]">System Health</div>
          <h1 className={`text-xl md:text-2xl font-semibold tracking-tight ${t.text}`} data-testid="sh-overall-label">{err || data?.label || "Checking…"}</h1>
          <div className="text-[12px] text-[color:var(--st-text-muted)]" data-testid="sh-generated-at">{data ? `Checked ${fmtSince(data.generated_at)} · ${new Date(data.generated_at).toLocaleTimeString()}` : ""}{auto ? " · auto-refresh 30 s" : ""}</div>
        </div>
        <label className="flex items-center gap-2 text-[12px]"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid="sh-auto-refresh" /> Auto-refresh</label>
        <button onClick={load} disabled={busy} className="h-9 px-3 rounded-full border border-[color:var(--st-border)] bg-white text-[12px] font-medium inline-flex items-center gap-1.5" data-testid="sh-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh</button>
        {isAdmin && <button onClick={runChecks} disabled={busy} className="h-9 px-3 rounded-full bg-[color:var(--st-text)] text-white text-[12px] font-medium inline-flex items-center gap-1.5" data-testid="sh-run-checks"><Play className="h-3.5 w-3.5" /> Run all checks</button>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="sh-component-grid">
        {!data && !err && [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="os-skeleton h-24 rounded-xl" />)}
        {(data?.components || []).map((c, i) => <ComponentCard key={c.key} c={c} index={i} active={tab === c.key} onClick={() => setTab(c.key)} />)}
      </div>

      <div className="os-card p-4">
        <div className="flex items-center gap-2 mb-2"><b className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)]">API traffic · last 60 min</b>
          <span className="ml-auto text-[11px] text-[color:var(--st-text-muted)]">{comp("application")?.metrics?.window_60m?.requests ?? 0} requests · {comp("application")?.metrics?.window_60m?.errors_5xx ?? 0} × 5xx</span></div>
        <TrafficChart data={data?.timeline || []} />
      </div>

      <div className="flex gap-1 border-b border-[color:var(--st-border)] overflow-x-auto" role="tablist" data-testid="sh-tabs">
        {TABS.map((x) => <button key={x.id} role="tab" aria-selected={tab === x.id} onClick={() => setTab(x.id)} data-testid={`sh-tab-btn-${x.id}`}
          className={`h-9 px-3 text-[12px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === x.id ? "border-[color:var(--st-text)] text-[color:var(--st-text)]" : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]"}`}>{x.label}</button>)}
      </div>

      {tab === "application" && <ApplicationTab comp={comp("application")} />}
      {tab === "database" && <DatabaseTab />}
      {tab === "api" && <ApiFailuresTab />}
      {tab === "jobs" && <JobsTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "logs" && <LogsTab isAdmin={isAdmin} />}
      {tab === "history" && <HistoryTab />}
      {user?.role === "super_admin" && <ReleaseArtifacts />}
    </div>
  );
}
